import type { ReactElement } from "react";
import type { SeoPage } from "../lib/seo";
import { formatInt } from "../lib/format";

type SeoPagesTableProps = {
  pages: SeoPage[];
};

export function SeoPagesTable({ pages }: SeoPagesTableProps) {
  if (pages.length === 0) {
    return (
      <div className="empty">
        <p>No pages crawled yet — the next scheduled crawl will populate this table.</p>
      </div>
    );
  }

  // Sort by: errors first, then most issues, then slowest.
  const sorted = [...pages].sort((a, b) => {
    const aError = (a.status_code ?? 0) >= 400 ? 1 : 0;
    const bError = (b.status_code ?? 0) >= 400 ? 1 : 0;
    if (aError !== bError) return bError - aError;
    const aIssues = a.issues?.length ?? 0;
    const bIssues = b.issues?.length ?? 0;
    if (aIssues !== bIssues) return bIssues - aIssues;
    return (b.response_time_ms ?? 0) - (a.response_time_ms ?? 0);
  });

  return (
    <div className="table-wrap">
      <table className="campaign-table">
        <thead>
          <tr>
            <th>Path</th>
            <th>Status</th>
            <th>Title</th>
            <th className="num">Issues</th>
            <th className="num">Words</th>
            <th className="num">Response</th>
          </tr>
        </thead>
        <tbody>
          {sorted.slice(0, 100).map((p) => {
            const statusKind = statusKindOf(p.status_code);
            return (
              <tr key={p.url}>
                <td>
                  <a href={p.url} target="_blank" rel="noopener noreferrer" className="page-link">
                    {p.path || "/"}
                  </a>
                </td>
                <td>
                  <span className="status-pill" data-status={statusKind}>
                    {p.status_code ?? "—"}
                  </span>
                </td>
                <td className="title-cell">{p.title ?? <em className="muted">(missing)</em>}</td>
                <td className="num">{renderIssueCount(p.issues)}</td>
                <td className="num">{p.word_count != null ? formatInt(p.word_count) : "—"}</td>
                <td className="num">{p.response_time_ms != null ? `${p.response_time_ms}ms` : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {sorted.length > 100 && (
        <p className="table-foot">Showing top 100 of {sorted.length} pages.</p>
      )}
    </div>
  );
}

function statusKindOf(code: number | null): string {
  if (code == null) return "removed";
  if (code >= 200 && code < 300) return "enabled";
  if (code >= 300 && code < 400) return "paused";
  return "removed";
}

function renderIssueCount(issues: SeoPage["issues"]): ReactElement {
  const count = issues?.length ?? 0;
  if (count === 0) return <span className="muted">—</span>;
  const hasError = issues.some((i) => i.severity === "error");
  const hasWarning = issues.some((i) => i.severity === "warning");
  const status = hasError ? "removed" : hasWarning ? "paused" : "enabled";
  return <span className="status-pill" data-status={status}>{count}</span>;
}
